/**
 * The RIVENA marks.
 *
 * These are the REAL artwork, cut from the brand identity sheet
 * (`docs/brand/identity-sheet.png`) rather than redrawn. An earlier version of this
 * file held hand-traced SVG paths approximating the logo from that same sheet; they
 * were close but not the mark, which is the kind of wrong that survives review and
 * then appears on a customer's printed report.
 *
 * ─── WHY TWO FILES INSTEAD OF ONE RECOLOURED ONE ───────────────────────────
 *
 * The wordmark ships as two PNGs, crimson and white, swapped by the `dark` class
 * Tailwind puts on <html>. A single asset tinted with CSS would need `mask-image`,
 * which costs a second decode path and fails silently in older Safari — leaving no
 * logo at all rather than the wrong colour. Two 33 KB files is the cheaper trade.
 *
 * Both are rendered at their natural aspect ratio with explicit width/height, so the
 * header does not reflow when they load.
 */
import { cn } from '../../lib/cn'

interface MarkProps {
  readonly className?: string
  /** Accessible name. Empty marks the image decorative, for use beside visible text. */
  readonly title?: string
}

/** Natural pixel dimensions of the extracted assets — used to reserve layout space. */
const LOCKUP = { width: 598, height: 162 }
const SYMBOL = { width: 155, height: 159 }

/**
 * The horizontal lockup: symbol plus wordmark. The primary mark — use it wherever
 * there is room for it.
 */
export function RivenaLockup({ className, title = 'RIVENA' }: MarkProps) {
  const alt = title
  return (
    <>
      <img
        src="/brand/rivena-lockup.png"
        alt={alt}
        width={LOCKUP.width}
        height={LOCKUP.height}
        className={cn('block h-auto w-auto dark:hidden', className)}
      />
      <img
        src="/brand/rivena-lockup-light.png"
        // The dark-mode twin of an image already announced above. Naming it again
        // would have a screen reader read the brand twice on every page.
        alt=""
        aria-hidden
        width={LOCKUP.width}
        height={LOCKUP.height}
        className={cn('hidden h-auto w-auto dark:block', className)}
      />
    </>
  )
}

/**
 * The symbol alone — the rounded crimson tile. For a collapsed sidebar, an avatar
 * slot, or anywhere the wordmark would be illegible. It carries its own background,
 * so it needs no accent-coloured container behind it.
 */
export function RivenaSymbol({ className, title = 'RIVENA' }: MarkProps) {
  return (
    <img
      src="/brand/rivena-symbol.png"
      alt={title}
      width={SYMBOL.width}
      height={SYMBOL.height}
      className={cn('block', className)}
    />
  )
}
